import z from 'zod';

import { Slide } from '@player/content';

import {
  SliderContent,
  SliderContentIn,
  SliderContentOut,
  SliderContentSchema,
  VideoContent,
  VideoContentIn,
  VideoContentOut,
  VideoContentSchema,
} from '@/features/content-form/lib/schema';
import { publisherApi } from '@/shared/api/publisherApi';
import type { Pagination } from '@/shared/api/types';

import {
  Content,
  ContentArg,
  ContentResponseDto,
  ContentStyles,
  ModifyContentDto,
  ModifyContentStylesDto,
  TextPosition,
} from './types';
import { toastError } from '@/shared/lib/toastManager';

const DEFAULT_PRESET: ContentStyles = {
  id: 0,
  type: 'video',
  name: 'Viads',
  config: {
    colors: {
      primary: '#0998cf',
      secondary: '#ffffff',
    },
    controls: {
      enabled: true,
      play_button: true,
      progress_bar: true,
      fullscreen_button: true,
      mute_button: true,
      navigation_buttons: true,
    },
    link_button: {
      enabled: true,
      color: '#ffffff',
      bg_color: '#234dff',
      text: 'Read more',
    },
    text: {
      font: 'Arial',
      color: '#ffffff',
      font_size: 18,
      bold: true,
      italic: false,
      position: TextPosition.Bottom,
    },
    logo: {
      enabled: false,
      over_video: true,
      size: 50,
      opacity: 50,
      position: 'top-right',
      file_path: '',
    },
    text_blocks_animation: {
      style: 'horizontal',
    },
    slides_animation: {
      style: 'fade-in',
      duration: 5,
    },
  },
};

function prepareSliderContent(params: SliderContent): FormData {
  const { type, config, slides, rss_settings, ...rest } = params;

  const result = new FormData();

  result.append('type', 'slider');
  result.append('config', JSON.stringify(config));
  result.append('data', JSON.stringify(rest));
  result.append('slides', JSON.stringify(slides));
  result.append('rss_settings', JSON.stringify(rss_settings));

  return result;
}

const VIDEO_MAX_SIZE_MB = 50;
function prepareVideoContent(params: VideoContent, video?: Blob): FormData {
  const { type, config, ...rest } = params;

  const result = new FormData();

  result.append('type', 'video');
  result.append('config', JSON.stringify(config));
  result.append('data', JSON.stringify(rest));

  if (video) {
    if (video.size > VIDEO_MAX_SIZE_MB * 1024 * 1024) {
      toastError(
        `Video must be at most ${VIDEO_MAX_SIZE_MB} MB. This file is ${(video.size / 1024 / 1024).toFixed(2)} MB.`
      );
      throw Error();
    }
    result.append('video', video, 'video.mp4');
  }
  const dataObject = Object.fromEntries(result.entries());
  console.log(dataObject);

  return result;
}

const prepareUpsertContentRequestBody = ({
  video,
  config,
  ...rest
}: ModifyContentDto): FormData => {
  const fd = new FormData();

  fd.append('type', rest.type);
  fd.append('config', JSON.stringify(config));
  fd.append('data', JSON.stringify(rest));

  if (rest.type === 'video') fd.append('video', video, 'video.mp4');

  if (rest.type === 'slider') {
    fd.append('slides', JSON.stringify(rest.slides));
    fd.append('rss_settings', JSON.stringify(rest.rss_settings));
  }

  return fd;
};

const prepareUploadFileRequestBody = (
  file: File,
  type: 'slide' | 'logo'
): FormData => {
  const fd = new FormData();

  fd.append('file', file);
  fd.append('type', type);

  return fd;
};

export const contentApi = publisherApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    // Storage
    storagePreview: build.query<ContentResponseDto<string>, string>({
      query: (key) => ({
        url: `/content/storage/${key}/preview`,
      }),
    }),

    // Content
    getContent: build.query<
      ContentResponseDto<Content[]> & Pagination,
      ContentArg
    >({
      query: (params) => ({
        url: '/content/contents',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: 'ContentList' as const,
                id,
              })),
              { type: 'ContentList', id: 'LIST' },
            ]
          : [{ type: 'ContentList', id: 'LIST' }],
    }),
    getContentById: build.query<ContentResponseDto<Content>, Content['id']>({
      query: (id) => ({
        url: `/content/contents/${id}`,
      }),
      providesTags: (_results, _error, arg) => [
        {
          type: 'ContentList',
          id: arg,
        },
      ],
    }),

    getSliderContent: build.query<SliderContentOut, string>({
      query: (id) => `/content/contents/${id}`,
      transformResponse(response: ContentResponseDto<SliderContentIn>) {
        return SliderContentSchema.parse(response.data);
      },
      providesTags: (_results, _error, arg) => [
        {
          type: 'ContentList',
          id: arg,
        },
      ],
      // we can't use this API with manual cache updates, lol:
      // TODO: find other way of validation before transform
      // rawResponseSchema: z.object({
      //   data: SliderContentSchema,
      // }),
      // onSchemaFailure: (error, info) => {
      //   console.log(
      //     'getContentExperimental.onSchemaFailure\n',
      //     error.issues,
      //     info
      //   );
      // },
    }),

    getVideoContent: build.query<VideoContentOut, string>({
      query: (id) => `/content/contents/${id}`,
      transformResponse(response: ContentResponseDto<VideoContentIn>) {
        return VideoContentSchema.parse(response.data);
      },
      providesTags: (_results, _error, arg) => [
        {
          type: 'ContentList',
          id: arg,
        },
      ],
      // we can't use this API with manual cache updates, lol:
      // TODO: find other way of validation before transform
      // rawResponseSchema: z.object({
      //   data: SliderContentSchema,
      // }),
      // onSchemaFailure: (error, info) => {
      //   console.log(
      //     'getContentExperimental.onSchemaFailure\n',
      //     error.issues,
      //     info
      //   );
      // },
    }),

    loadSlidesFromRss: build.query<
      ContentResponseDto<Slide[]>,
      { url?: string; limit?: number }
    >({
      query: ({ url, limit } = {}) => ({
        url: '/content/contents/rss',
        params: {
          ...(url ? { url } : {}),
          ...(typeof limit === 'number' ? { limit } : {}),
        },
      }),
    }),
    deleteContent: build.mutation<ContentResponseDto<boolean>, Content['id'][]>(
      {
        query: (ids) => ({
          url: `/content/contents?contentIds=${ids.join(',')}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_resp, _error, arg) =>
          arg.map((id) => ({
            type: 'ContentList',
            id,
          })),
      }
    ),
    copyContent: build.mutation<ContentResponseDto<number>, Content['id']>({
      query: (id) => ({
        url: `/content/contents/${id}`,
        method: 'POST',
      }),
      invalidatesTags: (_resp, _error, arg) => [
        { type: 'ContentList', id: arg },
      ],
    }),
    publishContent: build.mutation<ContentResponseDto<boolean>, Content['id']>({
      query: (id) => ({
        url: `/content/contents/${id}/publish`,
        method: 'POST',
      }),
      invalidatesTags: (_resp, _error, arg) => [
        { type: 'ContentList', id: arg },
      ],
    }),
    unpublishContent: build.mutation<
      ContentResponseDto<boolean>,
      Content['id']
    >({
      query: (id) => ({
        url: `/content/contents/${id}/unpublish`,
        method: 'POST',
      }),
      invalidatesTags: (_resp, _error, arg) => [
        { type: 'ContentList', id: arg },
      ],
    }),
    bulkUnpublishContent: build.mutation<
      ContentResponseDto<number>,
      Content['id'][]
    >({
      query: (ids) => ({
        url: `/content/contents/unpublish?contentIds=${ids.join(',')}`,
        method: 'POST',
      }),
      invalidatesTags: (_resp, _error, arg) =>
        arg.map((id) => ({
          type: 'ContentList',
          id,
        })),
    }),
    bulkPublishContent: build.mutation<
      ContentResponseDto<number>,
      Content['id'][]
    >({
      query: (ids) => ({
        url: `/content/contents/publish?contentIds=${ids.join(',')}`,
        method: 'POST',
      }),
      invalidatesTags: (_resp, _error, arg) =>
        arg.map((id) => ({
          type: 'ContentList',
          id,
        })),
    }),
    addContent: build.mutation<ContentResponseDto<number>, ModifyContentDto>({
      query: (data) => ({
        url: '/content/contents',
        method: 'POST',
        body: prepareUpsertContentRequestBody(data),
        formData: true,
      }),
      invalidatesTags: () => [{ type: 'ContentList', id: 'LIST' }],
    }),

    createSliderContent: build.mutation<
      ContentResponseDto<SliderContent>,
      SliderContent
    >({
      query: (data) => ({
        url: '/content/contents',
        method: 'POST',
        body: prepareSliderContent(data),
        formData: true,
      }),
      invalidatesTags: () => [{ type: 'ContentList', id: 'LIST' }],
    }),

    createVideoContent: build.mutation<
      ContentResponseDto<VideoContent>,
      VideoContent
    >({
      query: (data) => ({
        url: '/content/contents',
        method: 'POST',
        body: prepareVideoContent(data),
        formData: true,
      }),
      invalidatesTags: () => [{ type: 'ContentList', id: 'LIST' }],
    }),

    updateSliderContent: build.mutation<
      ContentResponseDto<SliderContent>,
      SliderContent & { id: string }
    >({
      query: (data) => ({
        url: `/content/contents/${data.id}`,
        method: 'PUT',
        body: prepareSliderContent(data),
        formData: true,
      }),
      invalidatesTags: () => [{ type: 'ContentList', id: 'LIST' }],
    }),

    updateVideoContent: build.mutation<
      ContentResponseDto<VideoContent>,
      VideoContent & { id: string; video?: Blob }
    >({
      query: (data) => ({
        url: `/content/contents/${data.id}`,
        method: 'PUT',
        body: prepareVideoContent(data, data.video),
        formData: true,
      }),
      invalidatesTags: () => [{ type: 'ContentList', id: 'LIST' }],
    }),

    updateContentById: build.mutation<
      ContentResponseDto<number>,
      ModifyContentDto & Pick<Content, 'id'>
    >({
      query: ({ id, ...data }) => ({
        url: `/content/contents/${id}`,
        method: 'PUT',
        body: prepareUpsertContentRequestBody(data),
        formData: true,
      }),
      invalidatesTags: (_response, _error, { id }) => [
        {
          type: 'ContentList',
          id,
        },
      ],
    }),

    // Content Styles
    getContentStyles: build.query<ContentResponseDto<ContentStyles[]>, void>({
      query: () => ({
        url: '/content/presets',
      }),
      transformResponse: (response: ContentResponseDto<ContentStyles[]>) => ({
        ...response,
        data: [DEFAULT_PRESET, ...response.data],
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: 'ContentStylesList' as const,
                id,
              })),
              { type: 'ContentStylesList', id: 'LIST' },
            ]
          : [{ type: 'ContentStylesList', id: 'LIST' }],
    }),
    deleteContentStylesById: build.mutation<
      ContentResponseDto<boolean>,
      ContentStyles['id']
    >({
      query: (id: ContentStyles['id']) => ({
        url: `/content/presets/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_resp, _error, arg) => [
        { type: 'ContentStylesList', id: arg },
      ],
    }),
    copyContentStylesById: build.mutation<
      ContentResponseDto<boolean>,
      ContentStyles['id']
    >({
      query: (id: ContentStyles['id']) => ({
        url: `/content/presets/${id}`,
        method: 'POST',
      }),
      invalidatesTags: () => [{ type: 'ContentStylesList', id: 'LIST' }],
    }),
    addContentStyles: build.mutation<
      ContentResponseDto<number>,
      ModifyContentStylesDto
    >({
      query: (data) => ({
        url: '/content/presets',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [{ type: 'ContentStylesList', id: 'LIST' }],
    }),
    updateContentStylesById: build.mutation<
      ContentResponseDto<number>,
      ModifyContentStylesDto & Pick<ContentStyles, 'id'>
    >({
      query: ({ id, ...data }) => ({
        url: `/content/presets/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_response, _error, { id }) => [
        {
          type: 'ContentStylesList',
          id,
        },
      ],
    }),
    getContentStylesById: build.query<
      ContentResponseDto<ContentStyles>,
      ContentStyles['id']
    >({
      query: (id: ContentStyles['id']) => ({
        url: `/content/presets/${id}`,
        method: 'GET',
      }),
      providesTags: (_results, _error, arg) => [
        {
          type: 'ContentStylesList',
          id: arg,
        },
      ],
    }),
    uploadFile: build.mutation<
      ContentResponseDto<{ path: string }>,
      { file: File; type: 'slide' | 'logo' }
    >({
      query: ({ file, type }) => ({
        url: '/content/contents/media',
        method: 'POST',
        body: prepareUploadFileRequestBody(file, type),
        formData: true,
      }),
    }),
  }),
});

export const {
  useStoragePreviewQuery,
  useLazyGetContentQuery,
  useGetContentByIdQuery,
  useCopyContentMutation,
  usePublishContentMutation,
  useUnpublishContentMutation,
  useBulkPublishContentMutation,
  useBulkUnpublishContentMutation,
  useDeleteContentMutation,
  useGetContentStylesQuery,
  useDeleteContentStylesByIdMutation,
  useCopyContentStylesByIdMutation,
  useAddContentStylesMutation,
  useUpdateContentStylesByIdMutation,
  useGetContentStylesByIdQuery,
  useAddContentMutation,
  useUpdateContentByIdMutation,
  useUploadFileMutation,

  useCreateSliderContentMutation,
  useUpdateSliderContentMutation,
  useGetSliderContentQuery,

  useCreateVideoContentMutation,
  useUpdateVideoContentMutation,
  useGetVideoContentQuery,
} = contentApi;
